import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import {
  CREATE_GIST_WITH_CONTENT,
  GET_ALL_GISTS,
  GET_ALL_SUBJECTS,
  GET_MY_DRAFT_GISTS,
} from "../../services/graphql/queriesMutations";
import styles from "./BulkAuthoringPage.module.css";

type SubjectOption = {
  id: number;
  publicId?: string | null;
  title: string;
  slug?: string | null;
  parentId?: number | null;
};

type SubjectsQueryData = {
  subjects: SubjectOption[];
};

type ParsedDraftNode = {
  localId: string;
  title: string;
  level: number;
  parentLocalId: string | null;
  rawBody: string;
  summary: string;
  bodyHtml: string;
};

type ImportReportItem = {
  localId: string;
  title: string;
  status: "created" | "failed";
  level: number;
  parentLocalId: string | null;
  gistId?: number;
  gistPublicId?: string | null;
  message?: string;
};

type CreateGistResult = {
  createGistWithContent: {
    id: number;
    publicId?: string | null;
    title: string;
    slug?: string | null;
    subjectId: number;
    parentId?: number | null;
    status: string;
  };
};

type CreateGistVariables = {
  input: {
    title: string;
    subjectId: number;
    parentId?: number;
    point: string;
    body: string;
    status: "DRAFT";
  };
};

const SAMPLE_DOCUMENT = `# Fraction Method Manual

This main page explains the method and links the whole guide together.

## Fraction as quantity

A fraction should first feel like one number that measures a quantity.

## Equivalent forms

The same amount can be written in different forms. This section explains why equivalent fractions are not magic.

### Multiplying by 1

Multiplying numerator and denominator by the same number is a controlled way to keep the value unchanged.

## Common denominator

A common denominator is a shared unit, not a separate rule.`;

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const stripMarkdown = (value: string): string =>
  value
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+[.)]\s+/gm, "")
    .replace(/[>*_~#]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const truncate = (value: string, maxLength: number): string => {
  const clean = value.trim();
  if (clean.length <= maxLength) return clean;
  return `${clean.slice(0, maxLength - 1).trim()}…`;
};

const toTitle = (value: string): string => {
  const clean = stripMarkdown(value).replace(/[:：]+$/g, "").trim();
  return clean || "Untitled draft";
};

const markdownBlockToHtml = (block: string): string => {
  const lines = block.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.length === 0) return "";

  const isBulletList = lines.every((line) => /^[-*+]\s+/.test(line));
  if (isBulletList) {
    const items = lines
      .map((line) => `<li>${escapeHtml(line.replace(/^[-*+]\s+/, ""))}</li>`)
      .join("");
    return `<ul>${items}</ul>`;
  }

  const isNumberedList = lines.every((line) => /^\d+[.)]\s+/.test(line));
  if (isNumberedList) {
    const items = lines
      .map((line) => `<li>${escapeHtml(line.replace(/^\d+[.)]\s+/, ""))}</li>`)
      .join("");
    return `<ol>${items}</ol>`;
  }

  return `<p>${lines.map(escapeHtml).join("<br />")}</p>`;
};

const markdownToHtml = (value: string, title: string): string => {
  const clean = value.trim();
  if (!clean) {
    return `<p>Draft placeholder for ${escapeHtml(title)}. Continue writing this section here.</p>`;
  }

  const html = clean
    .split(/\n\s*\n/g)
    .map(markdownBlockToHtml)
    .filter(Boolean)
    .join("\n");

  return html || `<p>Draft placeholder for ${escapeHtml(title)}. Continue writing this section here.</p>`;
};

const createSummary = (rawBody: string, title: string): string => {
  const firstParagraph = rawBody
    .split(/\n\s*\n/g)
    .map(stripMarkdown)
    .find((paragraph) => paragraph.length > 0);

  return truncate(firstParagraph || `Draft section for ${title}.`, 240);
};

const parseBulkMarkdown = (source: string): ParsedDraftNode[] => {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const nodes: Array<ParsedDraftNode & { rawLines: string[] }> = [];
  const stack: Array<{ localId: string; level: number }> = [];
  let current: (ParsedDraftNode & { rawLines: string[] }) | null = null;
  let counter = 0;

  const createNode = (level: number, title: string) => {
    while (stack.length > 0 && stack[stack.length - 1].level >= level) {
      stack.pop();
    }

    counter += 1;
    const node: ParsedDraftNode & { rawLines: string[] } = {
      localId: `draft-${counter}`,
      title: toTitle(title),
      level,
      parentLocalId: stack[stack.length - 1]?.localId ?? null,
      rawBody: "",
      summary: "",
      bodyHtml: "",
      rawLines: [],
    };

    nodes.push(node);
    stack.push({ localId: node.localId, level });
    current = node;
  };

  lines.forEach((line) => {
    const headingMatch = /^(#{1,6})\s+(.+?)\s*$/.exec(line);
    if (headingMatch) {
      createNode(headingMatch[1].length, headingMatch[2]);
      return;
    }

    if (!current && line.trim()) {
      createNode(1, "Untitled bulk draft");
    }

    current?.rawLines.push(line);
  });

  return nodes.map((node) => {
    const rawBody = node.rawLines.join("\n").trim();
    const summary = createSummary(rawBody, node.title);
    return {
      localId: node.localId,
      title: node.title,
      level: node.level,
      parentLocalId: node.parentLocalId,
      rawBody,
      summary,
      bodyHtml: markdownToHtml(rawBody, node.title),
    };
  });
};

const buildSubjectPath = (subject: SubjectOption, subjects: SubjectOption[]): string => {
  const byId = new Map(subjects.map((item) => [item.id, item]));
  const path: string[] = [subject.title];
  let cursor = typeof subject.parentId === "number" ? byId.get(subject.parentId) : null;
  let guard = 0;

  while (cursor && guard < 40) {
    path.unshift(cursor.title);
    cursor = typeof cursor.parentId === "number" ? byId.get(cursor.parentId) : null;
    guard += 1;
  }

  return path.join(" → ");
};

const buildDraftTreeUrl = (gistPublicId: string): string =>
  `/workspace/draft-tree?gistPublicId=${encodeURIComponent(gistPublicId)}`;

const normalizeSourceForImportKey = (value: string): string =>
  value.replace(/\s+/g, " ").trim().toLowerCase();

const createImportKey = (subjectId: number | null, sourceText: string): string =>
  `${subjectId ?? "no-subject"}::${normalizeSourceForImportKey(sourceText)}`;

const isUsingSampleDocument = (value: string): boolean =>
  normalizeSourceForImportKey(value) === normalizeSourceForImportKey(SAMPLE_DOCUMENT);

const BulkAuthoringPage: React.FC = () => {
  const [sourceText, setSourceText] = useState(SAMPLE_DOCUMENT);
  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(null);
  const [importReport, setImportReport] = useState<ImportReportItem[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [createConfirmed, setCreateConfirmed] = useState(false);
  const [lastCreatedImportKey, setLastCreatedImportKey] = useState<string | null>(null);
  const [lastCreatedSummary, setLastCreatedSummary] = useState<string | null>(null);
  const [copiedReport, setCopiedReport] = useState(false);

  const { data, loading, error } = useQuery<SubjectsQueryData>(GET_ALL_SUBJECTS, {
    fetchPolicy: "cache-and-network",
  });

  const [createGistWithContent] = useMutation<CreateGistResult, CreateGistVariables>(
    CREATE_GIST_WITH_CONTENT,
  );

  const subjects = useMemo(() => {
    return (data?.subjects ?? [])
      .slice()
      .sort((a, b) => buildSubjectPath(a, data?.subjects ?? []).localeCompare(buildSubjectPath(b, data?.subjects ?? [])));
  }, [data?.subjects]);

  useEffect(() => {
    if (selectedSubjectId !== null) return;
    if (subjects.length === 0) return;

    const simplestMath = subjects.find((subject) =>
      subject.title.toLowerCase().includes("simplestmath guide"),
    );
    setSelectedSubjectId((simplestMath || subjects[0]).id);
  }, [selectedSubjectId, subjects]);

  useEffect(() => {
    setCreateConfirmed(false);
    setImportError(null);
  }, [selectedSubjectId, sourceText]);

  const parsedDrafts = useMemo(() => parseBulkMarkdown(sourceText), [sourceText]);
  const selectedSubject = subjects.find((subject) => subject.id === selectedSubjectId) || null;
  const currentImportKey = useMemo(
    () => createImportKey(selectedSubjectId, sourceText),
    [selectedSubjectId, sourceText],
  );
  const createdCount = importReport.filter((item) => item.status === "created").length;
  const failedCount = importReport.filter((item) => item.status === "failed").length;
  const childDraftCount = parsedDrafts.filter((item) => item.parentLocalId).length;
  const createdRootItems = importReport.filter((item) => item.status === "created" && !item.parentLocalId && item.gistPublicId);
  const firstCreatedRoot = createdRootItems[0] ?? importReport.find((item) => item.status === "created" && item.gistPublicId) ?? null;
  const duplicateBlocked = lastCreatedImportKey === currentImportKey;
  const sampleDocumentActive = isUsingSampleDocument(sourceText);
  const canCreateDrafts =
    Boolean(selectedSubjectId) &&
    parsedDrafts.length > 0 &&
    createConfirmed &&
    !duplicateBlocked &&
    !isImporting;

  const handleSourceChange = (nextValue: string) => {
    setSourceText(nextValue);
    setImportReport([]);
  };

  const handleResetSample = () => {
    setSourceText(SAMPLE_DOCUMENT);
    setImportReport([]);
    setImportError(null);
  };

  const handleClearReport = () => {
    setImportReport([]);
    setImportError(null);
    setCopiedReport(false);
  };

  const handleCopyCreatedLinks = async () => {
    const createdItems = importReport.filter((item) => item.status === "created" && item.gistPublicId);
    if (createdItems.length === 0) return;

    const lines = createdItems.map((item) => {
      const indent = "  ".repeat(Math.max(0, item.level - 1));
      const url = `${window.location.origin}${buildDraftTreeUrl(item.gistPublicId as string)}`;
      return `${indent}- ${item.title}: ${url}`;
    });

    try {
      await window.navigator.clipboard.writeText(lines.join("\n"));
      setCopiedReport(true);
      window.setTimeout(() => setCopiedReport(false), 2400);
    } catch {
      window.prompt("Copy created draft links:", lines.join("\n"));
    }
  };

  const handleImportDraftTree = async () => {
    if (!selectedSubjectId) {
      setImportError("Choose a target subject before creating drafts.");
      return;
    }
    if (parsedDrafts.length === 0) {
      setImportError("Paste a document with at least one heading or paragraph.");
      return;
    }
    if (!createConfirmed) {
      setImportError("Confirm that you want to create private draft Gists from this preview.");
      return;
    }
    if (duplicateBlocked) {
      setImportError("This exact source and target subject were already imported in this browser session. Edit the source or choose a different subject before creating another draft tree.");
      return;
    }

    setImportError(null);
    setImportReport([]);
    setIsImporting(true);

    const createdIdByLocalId = new Map<string, number>();
    const nextReport: ImportReportItem[] = [];

    for (const draft of parsedDrafts) {
      try {
        const parentId = draft.parentLocalId ? createdIdByLocalId.get(draft.parentLocalId) : undefined;
        const result = await createGistWithContent({
          variables: {
            input: {
              title: draft.title,
              subjectId: selectedSubjectId,
              ...(parentId ? { parentId } : {}),
              point: draft.summary,
              body: draft.bodyHtml,
              status: "DRAFT",
            },
          },
          refetchQueries: [
            { query: GET_ALL_GISTS },
            { query: GET_ALL_SUBJECTS },
            { query: GET_MY_DRAFT_GISTS },
          ],
          awaitRefetchQueries: false,
        });

        const createdGist = result.data?.createGistWithContent;
        const gistId = createdGist?.id;
        const gistPublicId = createdGist?.publicId;
        if (!gistId || !gistPublicId) {
          throw new Error("The server did not return a created gist id and publicId.");
        }

        createdIdByLocalId.set(draft.localId, gistId);
        nextReport.push({
          localId: draft.localId,
          title: draft.title,
          status: "created",
          level: draft.level,
          parentLocalId: draft.parentLocalId,
          gistId,
          gistPublicId,
        });
      } catch (error) {
        nextReport.push({
          localId: draft.localId,
          title: draft.title,
          status: "failed",
          level: draft.level,
          parentLocalId: draft.parentLocalId,
          message: error instanceof Error ? error.message : "Unknown import error.",
        });
      }

      setImportReport([...nextReport]);
    }

    const successfulCreates = nextReport.filter((item) => item.status === "created").length;
    if (successfulCreates > 0) {
      setLastCreatedImportKey(currentImportKey);
      setLastCreatedSummary(`${successfulCreates} draft${successfulCreates === 1 ? "" : "s"} created inside ${selectedSubject?.title ?? "the selected subject"}.`);
    }
    setCreateConfirmed(false);
    setIsImporting(false);
  };

  return (
    <main className={styles.bulkPage}>
      <section className={styles.bulkShell}>
        <header className={styles.heroCard}>
          <div>
            <p className={styles.eyebrow}>Admin authoring lab</p>
            <h1>Bulk Gist Authoring</h1>
            <p className={styles.heroText}>
              Paste a Markdown outline, preview the Gist tree, then create the whole structure as private drafts.
              This version has a safety gate: preview first, confirm intentionally, then create drafts.
            </p>
          </div>
          <div className={styles.heroActions}>
            <Link className={styles.secondaryLink} to="/workspace/drafts">
              My Drafts
            </Link>
            <Link className={styles.secondaryLink} to="/workspace/drafts">
              Workspace
            </Link>
          </div>
        </header>

        <section className={styles.controlCard} aria-label="Bulk authoring controls">
          <div className={styles.subjectPickerGroup}>
            <label htmlFor="targetSubject">Target subject</label>
            {loading ? (
              <p className={styles.inlineHint}>Loading subjects…</p>
            ) : error ? (
              <p className={styles.inlineError}>Subjects could not be loaded: {error.message}</p>
            ) : (
              <select
                id="targetSubject"
                value={selectedSubjectId ?? ""}
                onChange={(event) => setSelectedSubjectId(Number(event.target.value))}
              >
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {buildSubjectPath(subject, subjects)}
                  </option>
                ))}
              </select>
            )}
            {selectedSubject && (
              <p className={styles.inlineHint}>
                New drafts will be created inside <strong>{selectedSubject.title}</strong>. Use headings to create parent/child Gists.
              </p>
            )}
          </div>

          <div className={styles.importStats}>
            <span>{parsedDrafts.length} draft{parsedDrafts.length === 1 ? "" : "s"} in preview</span>
            <span>{childDraftCount} child draft{childDraftCount === 1 ? "" : "s"}</span>
            <span>Mode: private drafts only</span>
          </div>
        </section>

        {lastCreatedSummary && (
          <section className={styles.statusBanner} aria-label="Last bulk authoring result">
            <strong>Last create run:</strong> {lastCreatedSummary}
          </section>
        )}

        <div className={styles.workspaceGrid}>
          <section className={styles.editorCard} aria-label="Source document">
            <div className={styles.cardHeaderRow}>
              <div>
                <p className={styles.eyebrow}>Source</p>
                <h2>Markdown outline</h2>
              </div>
              <button className={styles.smallButton} type="button" onClick={handleResetSample}>
                Reset sample
              </button>
            </div>
            {sampleDocumentActive && (
              <div className={styles.warningBox}>
                You are viewing the built-in sample. Creating drafts from it will add disposable sample Gists to your selected subject.
              </div>
            )}
            <textarea
              value={sourceText}
              onChange={(event) => handleSourceChange(event.target.value)}
              className={styles.sourceTextarea}
              spellCheck
              aria-label="Bulk authoring source document"
            />
            <p className={styles.inlineHint}>
              H1 becomes a root draft. H2 becomes a child of the nearest H1. H3 becomes a child of the nearest H2.
            </p>
          </section>

          <section className={styles.previewCard} aria-label="Draft tree preview">
            <div className={styles.cardHeaderRow}>
              <div>
                <p className={styles.eyebrow}>Preview only</p>
                <h2>Draft tree preview</h2>
              </div>
              <span className={styles.previewPill}>No drafts are created here</span>
            </div>

            {parsedDrafts.length === 0 ? (
              <div className={styles.emptyPreview}>Paste text with headings to preview draft Gists.</div>
            ) : (
              <ol className={styles.previewList}>
                {parsedDrafts.map((draft) => (
                  <li key={draft.localId} className={styles.previewItem} style={{ marginLeft: `${Math.max(0, draft.level - 1) * 18}px` }}>
                    <div className={styles.previewItemHeader}>
                      <span className={styles.levelBadge}>H{draft.level}</span>
                      <strong>{draft.title}</strong>
                    </div>
                    <p>{draft.summary}</p>
                    <small>{draft.parentLocalId ? "Child draft" : "Root draft"}</small>
                  </li>
                ))}
              </ol>
            )}

            <div className={styles.createPanel} aria-label="Create private draft tree">
              <div>
                <p className={styles.eyebrow}>Create step</p>
                <h3>Create private draft Gists</h3>
                <p>
                  This action writes to the database. It creates new private draft Gists only; it does not publish or delete anything.
                </p>
              </div>

              {duplicateBlocked && (
                <div className={styles.warningBox}>
                  This exact source and target subject were already created in this browser session. Edit the source or choose another subject to create a new draft tree.
                </div>
              )}

              {importError && <p className={styles.inlineError}>{importError}</p>}

              <label className={styles.confirmRow}>
                <input
                  type="checkbox"
                  checked={createConfirmed}
                  onChange={(event) => setCreateConfirmed(event.target.checked)}
                  disabled={isImporting || duplicateBlocked}
                />
                <span>
                  I reviewed the preview and want to create {parsedDrafts.length} private draft{parsedDrafts.length === 1 ? "" : "s"} inside {selectedSubject?.title ?? "the selected subject"}.
                </span>
              </label>

              <div className={styles.buttonRow}>
                <button
                  className={styles.primaryButton}
                  type="button"
                  onClick={handleImportDraftTree}
                  disabled={!canCreateDrafts}
                >
                  {isImporting ? "Creating drafts…" : "Create draft tree"}
                </button>
                <button className={styles.smallButton} type="button" onClick={handleClearReport}>
                  Clear report
                </button>
              </div>
            </div>
          </section>
        </div>

        {importReport.length > 0 && (
          <section className={styles.reportCard} aria-label="Import report">
            <div className={styles.cardHeaderRow}>
              <div>
                <p className={styles.eyebrow}>Create report</p>
                <h2>{createdCount} created · {failedCount} failed</h2>
              </div>
              <div className={styles.reportActions}>
                {firstCreatedRoot?.gistPublicId && (
                  <Link className={styles.primaryButton} to={buildDraftTreeUrl(firstCreatedRoot.gistPublicId)}>
                    Open draft tree
                  </Link>
                )}
                <button
                  className={styles.smallButton}
                  type="button"
                  onClick={handleCopyCreatedLinks}
                  disabled={createdCount === 0}
                >
                  {copiedReport ? "Copied links" : "Copy created links"}
                </button>
                <Link className={styles.secondaryLink} to="/workspace/drafts">
                  Open Drafts Center
                </Link>
                <button className={styles.smallButton} type="button" onClick={handleClearReport}>
                  Clear report
                </button>
              </div>
            </div>

            {createdCount > 0 && (
              <div className={styles.nextStepBox}>
                <strong>Next step:</strong> open the draft tree, check the generated structure, then continue writing from the workspace.
                These drafts are still private and can be edited before publishing.
              </div>
            )}

            <div className={styles.reportList}>
              {importReport.map((item) => (
                <div
                  key={item.localId}
                  className={item.status === "created" ? styles.reportItemSuccess : styles.reportItemError}
                  style={{ marginLeft: `${Math.max(0, item.level - 1) * 18}px` }}
                >
                  <div className={styles.reportItemBody}>
                    <span className={styles.reportLevelBadge}>H{item.level}</span>
                    <div>
                      <strong>{item.title}</strong>
                      <p>
                        {item.status === "created"
                          ? `${item.parentLocalId ? "Child draft" : "Root draft"} created with publicId ${item.gistPublicId}.`
                          : item.message}
                      </p>
                    </div>
                  </div>
                  {item.status === "created" && item.gistPublicId && (
                    <Link className={styles.reportTreeLink} to={buildDraftTreeUrl(item.gistPublicId)}>
                      Open tree
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </section>
    </main>
  );
};

export default BulkAuthoringPage;
